/**
 * Cómo opera la empresa: qué clase de negocio es y si vende con variantes.
 *
 * La plataforma nació en un restaurante, y en un restaurante el producto que
 * se vende no se guarda: la empanada se hace al momento con masa, guiso y
 * aceite, y lo que hay en la nevera son esos insumos. Por eso un producto con
 * receta no tiene saldo propio —«para cuántos me alcanza» se deriva—.
 *
 * Un comercio —una tienda de ropa, un ecommerce— es lo contrario: lo que se
 * compra es lo que se vende. La camisa que llega en la caja es la misma que
 * sale por la caja registradora, y lo que hay que saber es cuántas quedan de
 * ESA camisa. No hay receta, y el aviso de «sin receta» no es una advertencia:
 * es ruido.
 *
 * Por eso hay una casilla por producto, `lleva_existencias`: este producto
 * tiene su propio saldo. Y por eso hay un modo por empresa, que no manda sobre
 * lo ya guardado sino que decide el **valor inicial** de esa casilla en los
 * productos nuevos:
 *
 *   restaurante → apagada. Los productos se hacen con insumos.
 *   comercio    → encendida. Los productos se compran y se revenden.
 *   mixto       → apagada, y se enciende producto por producto: una cafetería
 *                 que además vende bolsas de café sellado.
 *
 * Cambiar el modo no toca los productos que ya existen. Escrito en positivo y
 * opcional: lo que no dice nada se comporta como ayer.
 *
 * Las variantes son un interruptor aparte porque son otra pregunta: un
 * restaurante puede querer tallas de delantal y un comercio puede no
 * necesitarlas. Ver `variantes.ts`.
 */
export const MODOS_OPERACION = [
    {
        valor: 'restaurante',
        texto: 'Restaurante o producción',
        nota: 'Lo que se vende se hace con insumos (receta). El inventario son los insumos.',
    },
    {
        valor: 'comercio',
        texto: 'Comercio o tienda en línea',
        nota: 'Lo que se compra es lo que se vende. Cada producto lleva su propio inventario.',
    },
    {
        valor: 'mixto',
        texto: 'Mixto',
        nota: 'Hay de las dos cosas. Se marca producto por producto cuál lleva inventario propio.',
    },
];
export const CONFIG_OPERACION_DEFECTO = {
    modo: 'restaurante',
    variantes: false,
    atributos: [],
};
const limpiaTexto = (t) => (typeof t === 'string' ? t.replace(/\s+/g, ' ').trim() : '');
/**
 * Lo guardado, tolerante: un documento ausente, viejo o a medias da la
 * configuración de siempre. Sin atributos repetidos ni vacíos; los valores de
 * cada uno, sin repetir sin importar mayúsculas.
 */
export function configOperacion(raw) {
    const modo = raw?.modo === 'comercio' || raw?.modo === 'mixto' ? raw.modo : 'restaurante';
    const vistos = new Set();
    const atributos = [];
    for (const a of Array.isArray(raw?.atributos) ? raw.atributos : []) {
        const nombre = limpiaTexto(a?.nombre);
        if (!nombre || vistos.has(nombre.toLowerCase()))
            continue;
        vistos.add(nombre.toLowerCase());
        const valores = [];
        for (const v of Array.isArray(a?.valores) ? a.valores : []) {
            const t = limpiaTexto(v);
            if (t && !valores.some((x) => x.toLowerCase() === t.toLowerCase()))
                valores.push(t);
        }
        atributos.push({ nombre, valores });
    }
    return { modo, variantes: raw?.variantes === true, atributos };
}
/** El valor inicial de «lleva existencias propias» para un producto nuevo. */
export function llevaExistenciasPorDefecto(modo) {
    return modo === 'comercio';
}
/**
 * ¿Este producto tiene saldo propio?
 *
 * Los insumos siempre —es lo que son—. Un producto de venta, solo si se marcó
 * `lleva_existencias`. El grupo de un producto con variantes nunca: es un
 * encabezado, y lo que se cuenta son sus variantes. Es **la** pregunta que contesta si el inventario se lee
 * sobre el producto o sobre sus insumos, así que está en un solo lugar: tres
 * pantallas que la respondan cada una a su manera acaban contradiciéndose.
 */
export function conSaldoPropio(p) {
    // Un grupo de variantes es solo el encabezado: sus existencias son las de sus variantes.
    return Boolean(p && !p.es_grupo && (p.es_insumo || p.lleva_existencias));
}
